import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronDown } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';

/**
 * Organization switcher. Lists every ACTIVE membership from /me (org name +
 * role) with the current org as the trigger. Selecting another org re-mints
 * the session cookie server-side, clears ALL cached queries (everything in
 * the cache is scoped to the old org), and lands the user on the dashboard
 * of the new org.
 *
 * Renders nothing for single-org users — there is nothing to switch to.
 */
export function OrgSwitcher() {
  const { state, switchOrg } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Click-outside-to-close (mirrors the Header account menu).
  useEffect(() => {
    if (!open) return undefined;
    function handler(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  if (state.status !== 'authenticated') return null;
  const memberships = state.me.memberships ?? [];
  if (memberships.length < 2) return null;

  const currentOrgId = state.me.organization.id;

  async function handleSelect(organizationId: string): Promise<void> {
    if (organizationId === currentOrgId || pendingOrgId !== null) return;
    setError(null);
    setPendingOrgId(organizationId);
    try {
      await switchOrg(organizationId);
      // Every cached query belongs to the OLD org — drop the lot before
      // anything refetches under the new tenant scope.
      queryClient.clear();
      setOpen(false);
      void navigate('/dashboard');
    } catch (err) {
      console.error('Org switch failed:', err);
      setError('Could not switch organization. Try again.');
    } finally {
      setPendingOrgId(null);
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        aria-label="Switch organization"
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex max-w-full items-center gap-1.5 rounded-kw-md px-1.5 py-1 text-[12px] text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
      >
        <Building2 className="size-3.5 shrink-0" />
        <span className="truncate font-medium">{state.me.organization.name}</span>
        <ChevronDown className="size-3.5 shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Organizations"
          className="absolute left-0 top-8 z-20 w-60 animate-fade-in rounded-kw-md border border-kw-border bg-white py-1 shadow-kw-md"
        >
          <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-kw-muted">
            Switch organization
          </p>
          {memberships.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={m.organizationId === currentOrgId}
              disabled={pendingOrgId !== null}
              onClick={() => {
                void handleSelect(m.organizationId);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-kw-bg disabled:cursor-wait disabled:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-kw-shark">{m.organization.name}</span>
                <span className="block text-[11px] capitalize text-kw-muted">{m.role.replace('_', ' ')}</span>
              </span>
              {pendingOrgId === m.organizationId ? (
                <span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
              ) : m.organizationId === currentOrgId ? (
                <Check className="size-4 shrink-0 text-kw-cerulean" />
              ) : null}
            </button>
          ))}
          {error ? <p className="px-3 py-1.5 text-[11px] text-kw-danger-text">{error}</p> : null}
        </div>
      )}
    </div>
  );
}
